'use client';

/**
 * CaptchaConfigProvider
 *
 * /api/captcha/config isteğini uygulama genelinde tek sefer yapar.
 * Tüm formlar (Login, Register, RequestForm, ContactForm, ProductReviewForm, ReservationCheckout vb.)
 * bu context üzerinden config alır; duplicate istekler (3x vb.) önlenir.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

export interface CaptchaConfig {
  provider: 'google_recaptcha_v2' | 'google_recaptcha_v3' | 'google_recaptcha_enterprise' | 'cloudflare_turnstile' | 'honeypot' | null;
  site_key: string | null;
  is_enabled: boolean;
  script_url: string | null;
}

export interface CaptchaConfigContextValue {
  config: CaptchaConfig | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const defaultConfig: CaptchaConfig = {
  provider: null,
  site_key: null,
  is_enabled: false,
  script_url: null,
};

export const CaptchaConfigContext = createContext<CaptchaConfigContextValue | undefined>(undefined);

const CAPTCHA_CONFIG_URL = '/api/captcha/config';

export function CaptchaConfigProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<CaptchaConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const pendingRef = useRef<Promise<void> | null>(null);

  const fetchConfig = useCallback(async () => {
    if (typeof window === 'undefined') return;

    const doFetch = async () => {
      try {
        const response = await fetch(CAPTCHA_CONFIG_URL, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });

        if (!response.ok) {
          throw new Error(`Captcha config failed: ${response.status}`);
        }

        const data = await response.json();
        const raw = (data.data ?? (data.success && data.data ? data.data : null)) as CaptchaConfig | null;

        if (raw && typeof raw === 'object') {
          setConfig({
            provider: raw.provider ?? null,
            site_key: raw.site_key ?? null,
            is_enabled: !!raw.is_enabled,
            script_url: raw.script_url ?? null,
          });
        } else {
          setConfig(defaultConfig);
        }
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Captcha config alınamadı');
        setConfig(defaultConfig);
      } finally {
        setIsLoading(false);
        pendingRef.current = null;
      }
    };

    if (pendingRef.current) {
      await pendingRef.current;
      return;
    }

    pendingRef.current = doFetch();
    await pendingRef.current;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;
    const run = () => {
      if (cancelled || fetchedRef.current) return;
      fetchedRef.current = true;
      void fetchConfig();
    };
    let idleId: ReturnType<typeof requestIdleCallback> | ReturnType<typeof setTimeout>;
    let scheduledWithIdleCallback = false;
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      scheduledWithIdleCallback = true;
      idleId = requestIdleCallback(run, { timeout: 2800 });
    } else {
      idleId = globalThis.setTimeout(run, 120);
    }
    return () => {
      cancelled = true;
      if (scheduledWithIdleCallback) {
        cancelIdleCallback(idleId as number);
      } else {
        clearTimeout(idleId as ReturnType<typeof setTimeout>);
      }
    };
  }, [fetchConfig]);

  const value: CaptchaConfigContextValue = {
    config,
    isLoading,
    error,
    refetch: fetchConfig,
  };

  return (
    <CaptchaConfigContext.Provider value={value}>
      {children}
    </CaptchaConfigContext.Provider>
  );
}

export function useCaptchaConfigContext(): CaptchaConfigContextValue {
  const ctx = useContext(CaptchaConfigContext);
  if (ctx === undefined) {
    throw new Error('useCaptchaConfigContext must be used within CaptchaConfigProvider');
  }
  return ctx;
}
